import { TuiInput, TuiTextfield } from '@taiga-ui/core';
import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, forkJoin, map, tap } from 'rxjs';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { FormPickerComponent } from '@core/components';
import { AttachExecutorsDirective } from './directives/attach-executors.directive';
import { AttachSubdivisionsDirective } from './directives/attach-subdivisions.directive';
import { AttachInstallersDirective } from './directives/attach-installers.directive';
import { AttachZonesDirective } from './directives/attach-zones.directive';
import { RequestDTO } from '@core/types/Request';
import { AsyncPipe } from '@angular/common';
import { compareById } from '@core/helpers/compareById';
import { RequestService } from '@core/services/request/request.service';
import { TranslatePipe } from '@ngx-translate/core';

enum FormFields {
  ProjectNumber = 'projectNumber',
  ContractorCounterpartyId = 'contractorCounterpartyId',
  ContractorCounterpartySubdivisionId = 'contractorCounterpartySubdivisionId',
  InstallZoneId = 'installZoneId',
  InstallZoneNameCashed = 'installZoneNameCashed',
  InstallPrice = 'installPrice',
  InstallerId = 'installerId',
}

type FormType = Pick<
  RequestDTO,
  | 'projectNumber'
  | 'contractorCounterpartyId'
  | 'contractorCounterpartySubdivisionId'
  | 'installZoneId'
  | 'installZoneNameCashed'
  | 'installPrice'
  | 'installerId'
>;

@Component({
  selector: 'x-request-form-common-main',
  imports: [
    TuiTextfield,
    AsyncPipe,
    ReactiveFormsModule,
    TuiInput,
    FormPickerComponent,
    AttachExecutorsDirective,
    AttachSubdivisionsDirective,
    AttachInstallersDirective,
    AttachZonesDirective,
    TranslatePipe,
  ],
  templateUrl: './request-form-common-main.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormCommonMainComponent implements OnInit {
  protected readonly permissionService = inject(OutkeepPermissionService);
  protected readonly requestService = inject(RequestService);
  protected readonly FormFields = FormFields;
  private readonly formBuilder = inject(FormBuilder);
  private readonly parentForm = inject(FormGroupDirective);
  private readonly requestAPIService = inject(RequestsApiService);
  private readonly destroyRef = inject(DestroyRef);
  readonly installersStringifyFn = (person: any): string =>
    [person?.lastName, person?.firstName].filter(Boolean).join(' ');
  protected formGroup = this.formBuilder.group<FormType>({
    [FormFields.ProjectNumber]: '',
    [FormFields.ContractorCounterpartyId]: null,
    [FormFields.ContractorCounterpartySubdivisionId]: null,
    [FormFields.InstallZoneId]: null,
    [FormFields.InstallZoneNameCashed]: '',
    [FormFields.InstallPrice]: '',
    [FormFields.InstallerId]: null,
  });

  $readOnly = input<boolean>(false, { alias: 'readOnly' });

  $hideExecutorBlock = signal<boolean>(true);
  $request = this.requestService.$request;

  constructor() {
    effect(() => {
      if (this.$readOnly()) {
        this.formGroup.disable();
      }
    });
  }

  @pure
  get contractorCounterpartyIdField() {
    return this.formGroup.controls[FormFields.ContractorCounterpartyId];
  }

  @pure
  get contractorCounterpartySubdivisionIdField() {
    return this.formGroup.controls[
      FormFields.ContractorCounterpartySubdivisionId
    ];
  }

  @pure
  get installZoneIdField() {
    return this.formGroup.controls[FormFields.InstallZoneId];
  }

  @pure
  get installZoneNameCashedField() {
    return this.formGroup.controls[FormFields.InstallZoneNameCashed];
  }

  @pure
  get installPriceField() {
    return this.formGroup.controls[FormFields.InstallPrice];
  }

  @pure
  get installerIdField() {
    return this.formGroup.controls[FormFields.InstallerId];
  }

  ngOnInit() {
    this.initForm();
    this.initFormSubs();
    this.initHideExecutorBlock();
  }

  private initForm() {
    this.formGroup.patchValue(this.$request());
    this.parentForm.form.addControl('main', this.formGroup);
  }

  private initFormSubs() {
    forkJoin([
      this.contractorCounterpartyIdField.valueChanges.pipe(
        distinctUntilChanged(compareById),
        tap(() => this.contractorCounterpartySubdivisionIdField.reset()),
      ),
      this.contractorCounterpartySubdivisionIdField.valueChanges.pipe(
        distinctUntilChanged(compareById),
        tap(() => {
          this.installZoneIdField.reset();
          this.installerIdField.reset();
        }),
      ),
      this.installZoneIdField.valueChanges.pipe(
        distinctUntilChanged(compareById),
        map(value => value?.price || null),
        tap(price => {
          this.installPriceField.setValue(price);
          this.switchInstallZonePriceDisableState();
          this.updateInstallZoneNameCached();
        }),
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    // TODO перенести сохранение установщика на бэкенд
    // this.installerIdField.valueChanges
    //   .pipe(
    //     startWith(this.installerIdField.value),
    //     pairwise(),
    //     tap(([prevInstaller, nextInstaller]) => {
    //       const createNewInstallerCbName = 'createNewInstaller';

    //       const isNewInstallerChosen =
    //         nextInstaller && prevInstaller?.id !== nextInstaller.id;

    //       if (isNewInstallerChosen) {
    //         this.RequestService.addRequestSaveCallback(
    //           createNewInstallerCbName,
    //           () =>
    //             this.requestAPIService.setInstallerToRequest$(
    //               this.$request().id,
    //               this.installerIdField.value.id,
    //             ),
    //         );
    //       }

    //       const wasInstallerJustResetToOld =
    //         nextInstaller?.id === this.$request()?.installerId?.id;

    //       if (!nextInstaller || wasInstallerJustResetToOld) {
    //         this.RequestService.removeRequestSaveCallback(
    //           createNewInstallerCbName,
    //         );
    //       }
    //     }),
    //     takeUntilDestroyed(this.destroyRef),
    //   )
    //   .subscribe();
  }

  private initHideExecutorBlock() {
    this.requestAPIService
      .getCantSelectExecutorStatus$()
      .pipe(
        tap(result => this.$hideExecutorBlock.set(result)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  switchInstallZonePriceDisableState() {
    return !this.installZoneIdField.value?.changable
      ? this.installPriceField.disable()
      : this.installPriceField.enable();
  }

  updateInstallZoneNameCached(
    newChosenInstallZone = this.installZoneIdField.value,
  ) {
    if (newChosenInstallZone) {
      this.installZoneNameCashedField.setValue(newChosenInstallZone.name);
    } else {
      this.installZoneNameCashedField.setValue(null);
    }
  }
}
